import DsAppIcon from "../components/ds/AppIcon";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiTrash2,
  FiImage,
  FiChevronDown,
  FiGift,
  FiSettings,
  FiGlobe,
  FiDollarSign,
  FiZap,
  FiLoader,
  FiBold,
  FiItalic,
  FiUnderline,
  FiList,
  FiSmile,
  FiCheck,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import {
  normalizeInstalledAppId,
  normalizeInstalledApps,
  PRODUCT_EXCLUDED_APP_IDS,
  getInstanceDisplayName,
  isAppSoldAlone,
  productInstanceIds,
  CONSULTING_APP_ID,
} from "../utils/mintSpaceApps";
import { updateAppPricing, deleteAppPricing } from "../api/mintSpace";
import { uploadImage, uploadVideo } from "../api/upload";
import { refusedUploadMessage } from "../utils/refusedUpload";
import { PopUpSelect } from "../components/ds/PopUpButton";

const PERIODS = [
  { value: "1w", label: "1 week" },
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "1y", label: "1 year" },
];
const QUICK_PRICES = [19.99, 39.99, 79.99];

// Preset labels for the product's call-to-action button. Owners pick one of these or choose
// "Custom…" to type their own. The stored value is the literal text shown to buyers.
const BUTTON_TEXT_PRESETS = [
  "Join now",
  "Join",
  "Get access",
  "Buy now",
  "Subscribe",
  "Get started",
  "Enroll now",
];
const CUSTOM_BUTTON_TEXT = "__custom__";

// GEL is the only supported currency.
const currencySymbol = () => "₾";

// Quick-insert emojis for the description editor.
const EMOJIS = [
  "😀", "😄", "😍", "🤩", "😎", "🥳", "🔥", "✨",
  "🎉", "👍", "🙌", "🙏", "💪", "✅", "⭐", "❤️",
  "🚀", "💡", "📈", "🎁", "💯", "👀", "⚡", "🏆",
];

/**
 * Lightweight WYSIWYG editor backed by a contentEditable surface. Stores HTML in `value`
 * and exposes a toolbar for bold / italic / underline / lists / emojis. (Uses the legacy
 * document.execCommand API, which is still supported across current browsers.)
 */
const RichTextEditor = ({ value, onChange, placeholder, isDark, divider, baseText }) => {
  const ref = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Sync external value into the editable surface without disturbing the caret while typing
  // (during typing value === innerHTML, so this is a no-op; it only fires on real resets).
  useEffect(() => {
    const el = ref.current;
    if (el && value !== el.innerHTML) el.innerHTML = value || "";
  }, [value]);

  const sync = () => onChange(ref.current?.innerHTML || "");
  const exec = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    sync();
  };
  const insertEmoji = (emoji) => {
    ref.current?.focus();
    document.execCommand("insertText", false, emoji);
    sync();
    setEmojiOpen(false);
  };

  const btn = `flex h-8 min-w-[2rem] items-center justify-center rounded-radius-xs px-2 text-subheadline transition ${
    "text-label-secondary hover:bg-fill-quaternary"
  }`;
  const sep = `mx-1 h-5 w-px bg-separator-default`;

  return (
    <div
      className={`mt-4 overflow-hidden rounded-radius-sm border ${divider} ${
        "bg-bg-inset"
      }`}
    >
      <style>{`
        .rte-content:empty:before { content: attr(data-placeholder); color: #94a3b8; pointer-events: none; }
        .rte-content ul { list-style: disc; margin: .25rem 0; padding-left: 1.5rem; }
        .rte-content ol { list-style: decimal; margin: .25rem 0; padding-left: 1.5rem; }
        .rte-content a { color: #14b8a6; text-decoration: underline; }
      `}</style>

      <div className={`flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 ${divider}`}>
        <button type="button" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} className={btn}>
          <FiBold className="h-4 w-4" />
        </button>
        <button type="button" title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} className={btn}>
          <FiItalic className="h-4 w-4" />
        </button>
        <button type="button" title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} className={btn}>
          <FiUnderline className="h-4 w-4" />
        </button>
        <span className={sep} />
        <button type="button" title="Bullet list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} className={btn}>
          <FiList className="h-4 w-4" />
        </button>
        <button type="button" title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} className={btn}>
          <span className="text-footnote-semibold">1.</span>
        </button>
        <span className={sep} />
        <div className="relative">
          <button type="button" title="Emoji" onMouseDown={(e) => e.preventDefault()} onClick={() => setEmojiOpen((o) => !o)} className={btn}>
            <FiSmile className="h-4 w-4" />
          </button>
          {emojiOpen && (
            <div
              className="absolute left-0 top-9 z-20 grid w-[240px] grid-cols-8 gap-[2px] rounded-radius-lg border border-separator-default bg-bg-raised p-space-8 shadow-overlay animate-ds-pop-in"
            >
              {EMOJIS.map((em) => (
                <button
                  key={em}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertEmoji(em)}
                  className="flex size-7 items-center justify-center rounded-radius-xs text-lg hover:bg-fill-quaternary"
                >
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        data-placeholder={placeholder}
        className={`rte-content min-h-[200px] px-4 py-3 text-subheadline leading-relaxed outline-none ${baseText}`}
      />
    </div>
  );
};

/**
 * Product editor. A product unlocks one or more of the apps the owner has installed
 * ("downloaded") in their Mint Space — except Creator Rewards, which can't be sold, and
 * Gateway, which is only ever sold on its own.
 *
 * First the apps, then the product: a media + details + FAQ form on the left and a live
 * product preview (title + description) plus the pricing/access configuration on the
 * right. Persists to MintSpace.appPricing via the backend.
 */
const MintSpaceAppPricingPage = ({
  isDark,
  setToast,
  mintSpace,
  onSaved,
  initialAppId,
  initialInstanceId,
  initialProductId,
}) => {
  const { t } = useTranslation();

  const installedInstances = useMemo(() => {
    const catalog = getMintSpaceAppStoreCatalog(t);
    return normalizeInstalledApps(mintSpace?.installedApps, mintSpace)
      .map((inst) => {
        const appId = normalizeInstalledAppId(inst.appId);
        if (PRODUCT_EXCLUDED_APP_IDS.includes(appId)) return null;
        const app = catalog.find((a) => a.id === appId);
        if (!app || app.comingSoon) return null;
        return {
          instanceId: inst.instanceId,
          appId,
          catalogApp: app,
          displayName: getInstanceDisplayName(mintSpace, inst, app.name),
          soldAlone: isAppSoldAlone(appId),
        };
      })
      .filter(Boolean);
  }, [t, mintSpace?.installedApps, mintSpace]);

  const resolvedInitialAppId =
    initialAppId &&
    installedInstances.some((i) => i.appId === normalizeInstalledAppId(initialAppId))
      ? normalizeInstalledAppId(initialAppId)
      : null;

  const resolvedInitialInstanceId = useMemo(() => {
    if (
      initialInstanceId &&
      installedInstances.some((i) => i.instanceId === initialInstanceId)
    ) {
      return initialInstanceId;
    }
    if (resolvedInitialAppId) {
      return (
        installedInstances.find((i) => i.appId === resolvedInitialAppId)?.instanceId || null
      );
    }
    return null;
  }, [initialInstanceId, resolvedInitialAppId, installedInstances]);

  // Opened to edit a saved product (the dashboard's "Edit"): read once, when opened.
  const [editedProduct] = useState(() =>
    initialProductId
      ? (mintSpace?.appPricing || []).find((p) => String(p._id) === String(initialProductId)) || null
      : null
  );

  // The apps the product unlocks, main app first. Opened from an app, it starts with that
  // one; opened to edit a product, with the apps it already unlocks.
  const [selectedInstanceIds, setSelectedInstanceIds] = useState(() =>
    editedProduct
      ? productInstanceIds(editedProduct, mintSpace).filter((id) =>
          installedInstances.some((i) => i.instanceId === id)
        )
      : resolvedInitialInstanceId
      ? [resolvedInitialInstanceId]
      : []
  );

  // Editing starts at the apps, so more can be added; Continue keeps everything saved.
  const [step, setStep] = useState(editedProduct ? "apps" : resolvedInitialInstanceId ? "form" : "apps");

  // Bumped to start a new product over from its apps' defaults. @see the loader below
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (!resolvedInitialInstanceId) return;
    setSelectedInstanceIds([resolvedInitialInstanceId]);
    setSelectedProductId(null);
    setFormKey((k) => k + 1);
    setStep("form");
  }, [resolvedInitialInstanceId]);

  const selectedInstances = selectedInstanceIds
    .map((id) => installedInstances.find((i) => i.instanceId === id))
    .filter(Boolean);
  const selectedNames = selectedInstances.map((i) => i.displayName).join(" + ");
  const isConsultingProduct = selectedInstances.some((i) => i.appId === CONSULTING_APP_ID);

  /**
   * Pick or drop an app. One sold alone (Gateway) takes the product to itself, and
   * picking anything else drops it.
   */
  const toggleInstance = (entry) =>
    setSelectedInstanceIds((prev) => {
      if (prev.includes(entry.instanceId)) return prev.filter((id) => id !== entry.instanceId);
      if (entry.soldAlone) return [entry.instanceId];
      const soldAlone = new Set(
        installedInstances.filter((i) => i.soldAlone).map((i) => i.instanceId)
      );
      return [...prev.filter((id) => !soldAlone.has(id)), entry.instanceId];
    });

  // Which product is being edited. null === creating a new product. Opening the editor
  // starts a fresh product unless it was opened to edit one; existing ones are also
  // reachable via the product chips.
  const [selectedProductId, setSelectedProductId] = useState(editedProduct?._id ?? null);

  /** The installed apps a saved product unlocks, by name. */
  const productAppNames = (product) =>
    productInstanceIds(product, mintSpace)
      .map((id) => installedInstances.find((i) => i.instanceId === id)?.displayName)
      .filter(Boolean)
      .join(" + ");

  /** Open a saved product, with the apps it unlocks. */
  const openProduct = (product, space = mintSpace) => {
    setSelectedInstanceIds(
      productInstanceIds(product, space).filter((id) =>
        installedInstances.some((i) => i.instanceId === id)
      )
    );
    setSelectedProductId(product._id);
    setStep("form");
  };

  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [accessType, setAccessType] = useState("paid"); // "free" | "paid"
  const [billingType, setBillingType] = useState("recurring"); // "recurring" | "one-time"
  const [price, setPrice] = useState("1");
  const [period, setPeriod] = useState("1m");
  const [currency, setCurrency] = useState("GEL");
  const [freeTrial, setFreeTrial] = useState(false);
  const [planSettingsOpen, setPlanSettingsOpen] = useState(false);
  // Advanced pricing options (live inside the collapsible panel)
  const [trialDays, setTrialDays] = useState("");
  const [billingCycles, setBillingCycles] = useState("");
  const [spotsLimit, setSpotsLimit] = useState("");
  const [consultingAccessMode, setConsultingAccessMode] = useState("time");
  const [consultingDurationDays, setConsultingDurationDays] = useState("30");
  const [consultingCallQuota, setConsultingCallQuota] = useState("1");
  const [buttonText, setButtonText] = useState("Join now");
  const [buttonTextCustom, setButtonTextCustom] = useState(false);
  const [acceptLocalCurrency, setAcceptLocalCurrency] = useState(false);
  const [customizePayments, setCustomizePayments] = useState(false);
  const [description, setDescription] = useState("");
  const [faq, setFaq] = useState([]); // [{ question, answer }]
  const [bannerImage, setBannerImage] = useState(""); // in-page media (image or video)
  const [bannerType, setBannerType] = useState("image"); // "image" | "video"
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [listingBanner, setListingBanner] = useState(""); // list thumbnail (image or video)
  const [listingBannerType, setListingBannerType] = useState("image"); // "image" | "video"
  const [listingBannerPreview, setListingBannerPreview] = useState(null);
  const [listingBannerUploading, setListingBannerUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const bannerInputRef = useRef(null);
  const listingBannerInputRef = useRef(null);

  // Read the latest mintSpace, and the apps picked, without re-running the loader after
  // every save or pick: changing a product's apps mustn't throw away what was typed.
  const mintSpaceRef = useRef(mintSpace);
  mintSpaceRef.current = mintSpace;
  const selectedNamesRef = useRef(selectedNames);
  selectedNamesRef.current = selectedNames;

  // Load the selected product (or sensible defaults for a new product, named after its apps).
  useEffect(() => {
    const defaultName = selectedNamesRef.current;
    if (!selectedProductId && !defaultName) return;
    const cfg = selectedProductId
      ? (mintSpaceRef.current?.appPricing || []).find(
          (p) => String(p._id) === String(selectedProductId)
        )
      : null;
    if (cfg) {
      setName(cfg.name || defaultName);
      setHeadline(cfg.headline || "");
      setAccessType(cfg.accessType || "paid");
      setBillingType(cfg.billingType || "recurring");
      setPrice(cfg.price != null ? String(cfg.price) : "1");
      setPeriod(cfg.period || "1m");
      setCurrency(cfg.currency || "GEL");
      setFreeTrial(!!cfg.freeTrial);
      setTrialDays(cfg.trialDays ? String(cfg.trialDays) : "");
      setBillingCycles(cfg.billingCycles ? String(cfg.billingCycles) : "");
      setSpotsLimit(cfg.spotsLimit ? String(cfg.spotsLimit) : "");
      // A saved label that isn't one of the presets was typed by hand → show the custom field.
      if (cfg.buttonText && !BUTTON_TEXT_PRESETS.includes(cfg.buttonText)) {
        setButtonTextCustom(true);
        setButtonText(cfg.buttonText);
      } else {
        setButtonTextCustom(false);
        setButtonText(cfg.buttonText || "Join now");
      }
      setDescription(cfg.description || "");
      setBannerImage(cfg.bannerImage || "");
      setBannerType(cfg.bannerType || "image");
      setBannerPreview(cfg.bannerImage || null);
      setListingBanner(cfg.listingBanner || "");
      setListingBannerType(cfg.listingBannerType || "image");
      setListingBannerPreview(cfg.listingBanner || null);
      setFaq(
        Array.isArray(cfg.faq)
          ? cfg.faq.map((f) => ({ question: f.question || "", answer: f.answer || "" }))
          : []
      );
      setConsultingAccessMode(cfg.consultingAccessMode || "time");
      setConsultingDurationDays(
        cfg.consultingDurationDays ? String(cfg.consultingDurationDays) : "30"
      );
      setConsultingCallQuota(cfg.consultingCallQuota ? String(cfg.consultingCallQuota) : "1");
    } else {
      setName(defaultName);
      setHeadline("");
      setAccessType("paid");
      setBillingType("recurring");
      setPrice("1");
      setPeriod("1m");
      setCurrency("GEL");
      setFreeTrial(false);
      setTrialDays("");
      setBillingCycles("");
      setSpotsLimit("");
      setButtonText("Join now");
      setButtonTextCustom(false);
      setDescription("");
      setBannerImage("");
      setBannerType("image");
      setBannerPreview(null);
      setListingBanner("");
      setListingBannerType("image");
      setListingBannerPreview(null);
      setFaq([]);
    }
  }, [selectedProductId, formKey]);

  // ----- theme tokens (kept consistent with the rest of the Mint Space pages) -----
  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const subtleText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const innerCard = "border-separator-default bg-bg-raised";
  const inputBase = "border-separator-default bg-bg-inset text-label-primary placeholder-label-tertiary focus:border-accent-default";
  const divider = "border-separator-default";

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || "1 month";
  const symbol = currencySymbol(currency);
  const priceLabel =
    accessType === "free"
      ? t("mintSpace.free") || "Free"
      : `${symbol}${price || "0"}${
          billingType === "recurring" ? ` / ${periodLabel.replace(/^1 /, "")}` : ""
        }`;

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxBytes = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setToast?.(
        isVideo
          ? t("actualHome.videoSizeMustBeLessThan100MB") || "Video size must be less than 100MB"
          : t("actualHome.imageSizeMustBeLessThan10MB") || "Image size must be less than 10MB"
      );
      e.target.value = "";
      return;
    }
    const prevType = bannerType;
    const objectUrl = URL.createObjectURL(file);
    setBannerPreview(objectUrl);
    setBannerType(isVideo ? "video" : "image");
    setBannerUploading(true);
    try {
      const res = isVideo
        ? await uploadVideo(file, "app-pricing")
        : await uploadImage(file, "app-pricing");
      if (!res?.success) throw new Error("Upload failed");
      URL.revokeObjectURL(objectUrl);
      setBannerImage(res.url);
      setBannerPreview(res.url);
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      setBannerPreview(bannerImage || null);
      setBannerType(prevType);
      setToast?.(refusedUploadMessage(error) || t("mintSpace.errorUpdatingImage") || "Error updating image");
    } finally {
      setBannerUploading(false);
      e.target.value = "";
    }
  };

  // Listing banner = the thumbnail shown in product lists (image or video). A video thumbnail
  // auto-plays on hover in the Discover / product-grid cards.
  const handleListingBannerChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxBytes = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setToast?.(
        isVideo
          ? t("actualHome.videoSizeMustBeLessThan100MB") || "Video size must be less than 100MB"
          : t("actualHome.imageSizeMustBeLessThan10MB") || "Image size must be less than 10MB"
      );
      e.target.value = "";
      return;
    }
    const prevType = listingBannerType;
    const objectUrl = URL.createObjectURL(file);
    setListingBannerPreview(objectUrl);
    setListingBannerType(isVideo ? "video" : "image");
    setListingBannerUploading(true);
    try {
      const res = isVideo
        ? await uploadVideo(file, "app-pricing")
        : await uploadImage(file, "app-pricing");
      if (!res?.success) throw new Error("Upload failed");
      URL.revokeObjectURL(objectUrl);
      setListingBanner(res.url);
      setListingBannerPreview(res.url);
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      setListingBannerPreview(listingBanner || null);
      setListingBannerType(prevType);
      setToast?.(refusedUploadMessage(error) || t("mintSpace.errorUpdatingImage") || "Error updating image");
    } finally {
      setListingBannerUploading(false);
      e.target.value = "";
    }
  };

  // ----- FAQ helpers -----
  const addFaqItem = () => setFaq((prev) => [...prev, { question: "", answer: "" }]);
  const updateFaqItem = (idx, field, value) =>
    setFaq((prev) => prev.map((f, i) => (i === idx ? { ...f, [field]: value } : f)));
  const removeFaqItem = (idx) => setFaq((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!name.trim()) {
      setToast?.(t("mintSpace.productNameRequired") || "Please add a product name");
      return;
    }
    if (!mintSpace?._id) {
      setToast?.(t("mintSpace.mintSpaceNotFound") || "Mint Space not found");
      return;
    }
    if (!selectedInstances.length) {
      setStep("apps");
      return;
    }
    const payload = {
      appInstanceIds: selectedInstances.map((i) => i.instanceId),
      // The main app, as a server from before products could hold several reads it.
      appId: selectedInstances[0].appId,
      appInstanceId: selectedInstances[0].instanceId,
      productId: selectedProductId || undefined,
      name: name.trim(),
      headline: headline.trim(),
      accessType,
      billingType,
      price: Number(price) || 0,
      period,
      currency,
      freeTrial,
      trialDays: freeTrial ? Math.max(0, Math.floor(Number(trialDays) || 0)) : 0,
      billingCycles: Math.max(0, Math.floor(Number(billingCycles) || 0)),
      spotsLimit: Math.max(0, Math.floor(Number(spotsLimit) || 0)),
      buttonText: buttonText.trim().slice(0, 30),
      bannerImage,
      bannerType,
      listingBanner,
      listingBannerType,
      description: description.trim(),
      faq: faq
        .map((f) => ({ question: f.question.trim(), answer: f.answer.trim() }))
        .filter((f) => f.question || f.answer),
      ...(isConsultingProduct
        ? {
            consultingAccessMode: consultingAccessMode || "time",
            consultingDurationDays: Math.max(1, Math.floor(Number(consultingDurationDays) || 30)),
            consultingCallQuota: Math.max(1, Math.floor(Number(consultingCallQuota) || 1)),
          }
        : {
            consultingAccessMode: "",
            consultingDurationDays: 30,
            consultingCallQuota: 1,
          }),
    };
    try {
      setSaving(true);
      const res = await updateAppPricing(mintSpace._id, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      onSaved?.(res.data);
      if (res.productId) setSelectedProductId(res.productId);
      setToast?.(t("mintSpace.pricingSaved") || "Pricing saved");
    } catch (error) {
      setToast?.(error?.message || t("mintSpace.errorSavingPricing") || "Error saving pricing");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // Nothing persisted yet — just clear the form back to a fresh product.
    if (!selectedProductId) {
      setSelectedProductId(null);
      return;
    }
    if (!mintSpace?._id) return;
    try {
      setSaving(true);
      const res = await deleteAppPricing(mintSpace._id, selectedProductId);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      onSaved?.(res.data);
      const next = (res.data?.appPricing || []).find((p) =>
        productInstanceIds(p, res.data).some((id) => selectedInstanceIds.includes(id))
      );
      if (next) openProduct(next, res.data);
      else setSelectedProductId(null);
      setToast?.(t("mintSpace.productDeleted") || "Product deleted");
    } catch (error) {
      setToast?.(error?.message || t("mintSpace.errorDeletingProduct") || "Error deleting product");
    } finally {
      setSaving(false);
    }
  };

  // Reusable iOS-style switch
  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        disabled ? "opacity-40 cursor-not-allowed" : ""
      } ${
        checked
          ? "bg-accent-default"
          : "bg-fill-primary"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );

  const SelectShell = ({ value, onChange, children, className = "" }) => (
    <PopUpSelect value={value} onChange={onChange} className={`w-full ${className}`}>
      {children}
    </PopUpSelect>
  );

  if (installedInstances.length === 0) {
    return (
      <div className={`rounded-radius-lg border p-10 text-center ${cardBase}`}>
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-radius-md bg-accent-default">
          <FiDollarSign className="h-7 w-7 text-accent-on" />
        </div>
        <h2 className={`text-title-2 ${baseText}`}>
          {t("mintSpace.noAppsToPrice") || "No apps to price yet"}
        </h2>
        <p className={`mt-2 text-subheadline ${subtleText}`}>
          {t("mintSpace.noAppsToPriceHint") ||
            "Add an app from the App Store first, then come back here to set its price."}
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------- STEP 1: choose apps
  if (step === "apps") {
    return (
      <div className="w-full max-w-2xl">
        <h2 className={`text-title-2 ${baseText}`}>
          {t("mintSpace.chooseAppInstance") || "Choose apps"}
        </h2>
        <p className={`mt-1 text-subheadline ${subtleText}`}>
          {selectedProductId
            ? t("mintSpace.chooseAppsForProduct", {
                name: name.trim() || t("mintSpace.untitledProduct") || "Untitled product",
              })
            : t("mintSpace.chooseAppInstanceHint")}
        </p>

        <div className="mt-5 space-y-3">
          {installedInstances.map((entry) => {
            const Icon = entry.catalogApp.icon;
            const checked = selectedInstanceIds.includes(entry.instanceId);
            return (
              <button
                key={entry.instanceId}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleInstance(entry)}
                className={`flex w-full items-center justify-between gap-3 rounded-radius-md border px-4 py-4 text-left transition ${
                  checked ? "border-accent-default bg-mint/[0.06]" : `${innerCard} hover:border-mint/40`
                }`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <DsAppIcon app={entry.catalogApp.id} size={36} />
                  <span className="min-w-0">
                    <span className={`block truncate text-subheadline-semibold ${baseText}`}>
                      {entry.displayName}
                    </span>
                    <span className={`block text-footnote ${mutedText}`}>
                      {entry.catalogApp.name}
                      {entry.soldAlone && (
                        <span className="text-status-warning"> · {t("mintSpace.soldAlone")}</span>
                      )}
                    </span>
                  </span>
                </span>
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition ${
                    checked ? "border-accent-default bg-accent-default text-accent-on" : divider
                  }`}
                >
                  {checked && <FiCheck className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>

        {selectedInstances.some((i) => i.soldAlone) && (
          <p className={`mt-3 text-footnote ${mutedText}`}>{t("mintSpace.soldAloneHint")}</p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            disabled={!selectedInstances.length}
            onClick={() => {
              // A new product starts from its apps' defaults; one being edited keeps what was typed.
              if (!selectedProductId) setFormKey((k) => k + 1);
              setStep("form");
            }}
            className="inline-flex items-center gap-2 rounded-radius-sm bg-accent-default px-6 py-2.5 text-subheadline-semibold text-accent-on transition disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {t("common.continue") || "Continue"}
          </button>
        </div>

        {(mintSpace?.appPricing || []).length > 0 && (
          <div className={`mt-8 border-t pt-6 ${divider}`}>
            <h3 className={`text-subheadline-semibold ${baseText}`}>
              {t("mintSpace.yourProducts") || "Your products"}
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {(mintSpace?.appPricing || [])
                .filter((p) => !PRODUCT_EXCLUDED_APP_IDS.includes(normalizeInstalledAppId(p.appId)))
                .map((p) => {
                const apps = productAppNames(p);
                return (
                  <button
                    key={p._id}
                    type="button"
                    onClick={() => openProduct(p)}
                    className={`flex items-center gap-2 rounded-radius-sm border px-3.5 py-2 text-subheadline-semibold transition ${innerCard} ${subtleText} hover:border-accent-default`}
                  >
                    {p.name || t("mintSpace.untitledProduct") || "Untitled product"}
                    {apps && <span className={`text-footnote ${mutedText}`}>· {apps}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------- STEP 2: checkout setup

  return (
    <div className="w-full">
      <div className="mb-space-24 rounded-radius-lg bg-bg-raised p-space-16">
        <div className="flex flex-wrap items-center justify-between gap-space-12">
          <div className="flex min-w-0 items-center gap-space-12">
            {selectedInstances.length > 0 && (
              <>
                <span className="flex shrink-0 -space-x-2">
                  {selectedInstances.slice(0, 3).map((inst) => {
                    return (
                      <DsAppIcon key={inst.instanceId} app={inst.catalogApp.id} size={44} className="ring-2 ring-bg-base" />
                    );
                  })}
                </span>
                <div className="min-w-0">
                  <h2 className="truncate text-headline text-label-primary">{selectedNames}</h2>
                  <p className="text-footnote text-label-secondary">
                    {selectedInstances.length === 1
                      ? selectedInstances[0].catalogApp.name
                      : t("mintSpace.productAppsCount", { n: selectedInstances.length })}
                  </p>
                </div>
              </>
            )}
          </div>
          <Button variant="gray" size="s" icon={<Icon name="arrow-left" size={16} />} onClick={() => setStep("apps")}>
            {t("mintSpace.changeAppInstance") || "Change apps"}
          </Button>
        </div>
      </div>

      <div className="mb-space-24 flex flex-wrap items-center gap-space-8">
        {(mintSpace?.appPricing || [])
          .filter((p) =>
            productInstanceIds(p, mintSpace).some((id) => selectedInstanceIds.includes(id))
          )
          .map((p) => {
            const active = String(p._id) === String(selectedProductId);
            return (
              <button
                key={p._id}
                type="button"
                onClick={() => !active && openProduct(p)}
                className={`inline-flex h-[40px] items-center rounded-[22px] px-space-16 text-caps-button-m uppercase transition-colors ${
                  active ? "bg-accent-tinted text-accent-tinted-label" : "bg-fill-secondary text-label-primary hover:bg-fill-tertiary"
                }`}
              >
                {p.name || t("mintSpace.untitledProduct") || "Untitled product"}
              </button>
            );
          })}
        <Button
          variant={selectedProductId === null ? "tinted" : "gray"}
          size="m"
          icon={<Icon name="plus" size={20} />}
          onClick={() => setSelectedProductId(null)}
        >
          {t("mintSpace.newProduct") || "New product"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-space-24 lg:grid-cols-2">
        {/* ---------------------------------------------------------------- LEFT: media + details + FAQ */}
        <div className="min-w-0 space-y-4">
          {/* Media (image / video) */}
          <section>
            <div
              className={`relative flex h-40 w-full items-center justify-center overflow-hidden rounded-radius-md border ${divider} ${
                "bg-fill-tertiary"
              }`}
            >
              {bannerPreview ? (
                bannerType === "video" ? (
                  <video
                    src={bannerPreview}
                    className="h-full w-full object-cover"
                    controls
                    muted
                    playsInline
                  />
                ) : (
                  <img src={bannerPreview} alt="" className="h-full w-full object-cover" />
                )
              ) : null}

              {/* Upload / replace trigger (kept out of the way so video controls stay usable) */}
              {bannerPreview ? (
                <button
                  type="button"
                  onClick={() => !bannerUploading && bannerInputRef.current?.click()}
                  className="absolute right-3 top-3 z-10 rounded-radius-xs bg-black/60 px-3 py-1.5 text-footnote-semibold text-white backdrop-blur transition hover:bg-black/70"
                >
                  {t("mintSpace.changeMedia") || "Change"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => !bannerUploading && bannerInputRef.current?.click()}
                  className={`flex items-center gap-2 text-subheadline font-medium ${mutedText}`}
                >
                  <FiImage className="h-5 w-5" />
                  {t("mintSpace.addBannerMedia") || "Add image or video"}
                </button>
              )}

              {bannerUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
                </div>
              )}
            </div>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleBannerChange}
              className="hidden"
            />
          </section>

          {/* Description */}
          <section>
            <h2 className={`text-title-3 ${baseText}`}>
              {t("mintSpace.description") || "Description"}
            </h2>
            <p className={`mt-1 text-footnote text-label-secondary`}>
              {t("mintSpace.descriptionHint") ||
                "Tell buyers everything they get with this product."}
            </p>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder={t("mintSpace.addDescription") || "Add a description…"}
              isDark={isDark}
              divider={divider}
              baseText={baseText}
            />
          </section>

          {/* FAQ editor */}
          <section>
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`text-title-3 ${baseText}`}>{t("mintSpace.faq") || "FAQ"}</h2>
                <p className={`mt-1 text-footnote text-label-secondary`}>
                  {t("mintSpace.faqEditorHint") || "Answer common questions buyers may have."}
                </p>
              </div>
              <Button size="s" variant="gray" icon={<Icon name="plus" size={16} />} onClick={addFaqItem}>
                {t("mintSpace.addQuestion")}
              </Button>
            </div>

            {faq.length === 0 ? (
              <p className={`mt-4 text-subheadline ${mutedText}`}>
                {t("mintSpace.noFaqYet") || "No questions yet. Add your first one."}
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {faq.map((item, idx) => (
                  <div key={idx} className={`rounded-radius-md border p-4 ${innerCard}`}>
                    <div className="flex items-start gap-2">
                      <input
                        value={item.question}
                        onChange={(e) => updateFaqItem(idx, "question", e.target.value.slice(0, 300))}
                        placeholder={t("mintSpace.faqQuestionPlaceholder") || "Question"}
                        className={`flex-1 rounded-radius-sm border px-3.5 py-2.5 text-subheadline-semibold outline-none transition ${inputBase}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeFaqItem(idx)}
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-radius-xs transition ${
                          "text-label-secondary hover:bg-fill-quaternary hover:text-status-danger"
                        }`}
                        aria-label={t("common.remove") || "Remove"}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <textarea
                      value={item.answer}
                      onChange={(e) => updateFaqItem(idx, "answer", e.target.value.slice(0, 2000))}
                      placeholder={t("mintSpace.faqAnswerPlaceholder") || "Answer"}
                      rows={2}
                      className={`mt-2 w-full resize-none rounded-radius-sm border px-3.5 py-2.5 text-subheadline outline-none transition ${inputBase}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* ---------------------------------------------------------------- RIGHT: preview (title + description) + pricing */}
        <aside className="min-w-0 space-y-3">
          <div>
            <h2 className={`text-title-3 ${baseText}`}>
              {t("mintSpace.productPreview") || "Product preview"}
            </h2>

            {/* Listing preview card — its banner is the thumbnail shown in product lists */}
            <div className={`mt-4 overflow-hidden rounded-radius-md border ${cardBase}`}>
              <div
                className={`relative flex h-36 w-full items-center justify-center overflow-hidden ${
                  "bg-fill-tertiary"
                }`}
              >
                {listingBannerPreview ? (
                  listingBannerType === "video" ? (
                    <video
                      src={listingBannerPreview}
                      className="h-full w-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img src={listingBannerPreview} alt="" className="h-full w-full object-cover" />
                  )
                ) : null}

                {listingBannerPreview ? (
                  <button
                    type="button"
                    onClick={() => !listingBannerUploading && listingBannerInputRef.current?.click()}
                    className="absolute right-3 top-3 z-10 rounded-radius-xs bg-black/60 px-3 py-1.5 text-footnote-semibold text-white backdrop-blur transition hover:bg-black/70"
                  >
                    {t("mintSpace.changeMedia") || "Change"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => !listingBannerUploading && listingBannerInputRef.current?.click()}
                    className={`flex items-center gap-2 text-subheadline font-medium ${mutedText}`}
                  >
                    <FiImage className="h-4 w-4" />
                    {t("mintSpace.addBannerMedia") || "Add image or video"}
                  </button>
                )}

                {listingBannerUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
                  </div>
                )}
              </div>
              <input
                ref={listingBannerInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleListingBannerChange}
                className="hidden"
              />
              <div className="p-4">
                <h3 className={`text-title-3 ${baseText}`}>
                  {name || t("mintSpace.productNamePlaceholder") || "Product name"}
                </h3>
                {headline && <p className={`mt-0.5 text-subheadline ${subtleText}`}>{headline}</p>}
                <p className={`mt-1 text-subheadline-semibold ${subtleText}`}>{priceLabel}</p>
                <button
                  type="button"
                  className="mt-3 w-full rounded-radius-sm bg-accent-default py-2.5 text-subheadline-semibold text-accent-on transition hover:opacity-90"
                >
                  {buttonText.trim() || t("mintSpace.joinNow") || "Join now"}
                </button>
              </div>
            </div>
          </div>

          {/* Product details: name + headline */}
          <section>
            <h2 className={`text-title-3 ${baseText}`}>
              {t("mintSpace.productDetails") || "Product details"}
            </h2>
            <p className={`mt-1 text-footnote text-label-secondary`}>
              {t("mintSpace.productDetailsHint") || "Describe your product offering."}
            </p>

            <div className="mt-5 space-y-5">
              {/* Name */}
              <div>
                <label className={`block text-footnote-semibold text-label-secondary`}>
                  {t("mintSpace.name") || "Name"} <span className="text-status-danger">*</span>
                </label>
                <div className="relative mt-2">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 80))}
                    placeholder={t("mintSpace.productNamePlaceholder") || "Product name"}
                    className={`w-full rounded-radius-sm border px-4 py-3 pr-12 text-subheadline outline-none transition ${inputBase}`}
                  />
                  <button
                    type="button"
                    title={t("mintSpace.generateWithAI") || "Generate with AI"}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-radius-xs bg-accent-default text-accent-on transition hover:opacity-90"
                  >
                    <FiZap className="h-4 w-4" />
                  </button>
                </div>
                <div className={`mt-1 text-right text-footnote ${mutedText}`}>{name.length} / 80</div>
              </div>

              {/* Headline */}
              <div>
                <label className={`block text-footnote-semibold text-label-secondary`}>
                  {t("mintSpace.headline") || "Headline"}
                </label>
                <div className="relative mt-2">
                  <input
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value.slice(0, 80))}
                    placeholder={t("mintSpace.headlinePlaceholder") || "A short, punchy tagline"}
                    className={`w-full rounded-radius-sm border px-4 py-3 pr-12 text-subheadline outline-none transition ${inputBase}`}
                  />
                  <button
                    type="button"
                    title={t("mintSpace.generateWithAI") || "Generate with AI"}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-radius-xs bg-accent-default text-accent-on transition hover:opacity-90"
                  >
                    <FiZap className="h-4 w-4" />
                  </button>
                </div>
                <div className={`mt-1 text-right text-footnote ${mutedText}`}>{headline.length} / 80</div>
              </div>

              {/* Button text — the label shown on the buyer's join/buy button */}
              <div>
                <label className={`block text-footnote-semibold text-label-secondary`}>
                  {t("mintSpace.buttonText") || "Button text"}
                </label>
                <p className={`mt-1 text-footnote text-label-secondary`}>
                  {t("mintSpace.buttonTextHint") ||
                    "The label buyers see on the call-to-action button."}
                </p>
                <SelectShell
                  className="mt-2"
                  value={buttonTextCustom ? CUSTOM_BUTTON_TEXT : buttonText}
                  onChange={(val) => {
                    if (val === CUSTOM_BUTTON_TEXT) {
                      setButtonTextCustom(true);
                      setButtonText("");
                    } else {
                      setButtonTextCustom(false);
                      setButtonText(val);
                    }
                  }}
                >
                  {BUTTON_TEXT_PRESETS.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                  <option value={CUSTOM_BUTTON_TEXT}>
                    {t("mintSpace.customButtonText") || "Custom…"}
                  </option>
                </SelectShell>
                {buttonTextCustom && (
                  <div className="mt-2">
                    <input
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value.slice(0, 30))}
                      placeholder={t("mintSpace.buttonTextPlaceholder") || "e.g. Get instant access"}
                      className={`w-full rounded-radius-sm border px-4 py-3 text-subheadline outline-none transition ${inputBase}`}
                    />
                    <div className={`mt-1 text-right text-footnote ${mutedText}`}>
                      {buttonText.length} / 30
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className={`border-t ${divider}`} />

          {/* Pricing */}
          <section>
            <h2 className={`text-title-3 ${baseText}`}>
              {t("mintSpace.pricing") || "Pricing"}
            </h2>
            <p className={`mt-1 text-footnote text-label-secondary`}>
              {t("mintSpace.pricingHint") || "Choose how people access this product."}
            </p>

            {/* Access type cards */}
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { id: "free", icon: FiGlobe, label: t("mintSpace.freeAccess") || "Free access" },
                { id: "paid", icon: FiDollarSign, label: t("mintSpace.paidAccess") || "Paid access" },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = accessType === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setAccessType(opt.id)}
                    className={`flex items-center justify-between rounded-radius-md border px-4 py-4 text-left transition ${
                      active ? "border-accent-default bg-mint/[0.06]" : `${innerCard} hover:border-mint/40`
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-full border ${
                          active ? "border-accent-default text-accent-text" : `${divider} ${subtleText}`
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className={`text-subheadline-semibold ${baseText}`}>{opt.label}</span>
                    </span>
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        active ? "border-accent-default" : divider
                      }`}
                    >
                      {active && <span className="h-2.5 w-2.5 rounded-full bg-accent-default" />}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Paid pricing detail card */}
            {accessType === "paid" && (
              <div className={`mt-4 rounded-radius-md border p-5 ${innerCard}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-headline ${baseText}`}>{priceLabel}</span>
                  <button
                    type="button"
                    onClick={() => setAccessType("free")}
                    className="text-status-danger transition hover:text-status-danger"
                    title={t("common.remove") || "Remove"}
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Billing type */}
                <div className="mt-4">
                  <SelectShell value={billingType} onChange={setBillingType}>
                    <option value="recurring">{t("mintSpace.recurring") || "Recurring"}</option>
                    <option value="one-time">{t("mintSpace.oneTime") || "One-time"}</option>
                  </SelectShell>
                </div>

                {/* price / period (currency is always GEL — shown via the ₾ prefix) */}
                <div
                  className={`mt-3 grid gap-3 ${
                    billingType === "recurring" ? "grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  <div className="relative">
                    <span
                      className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subheadline ${mutedText}`}
                    >
                      {symbol}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className={`w-full rounded-radius-sm border py-2.5 pl-7 pr-3 text-subheadline outline-none transition ${inputBase}`}
                    />
                  </div>
                  {billingType === "recurring" && (
                    <SelectShell value={period} onChange={setPeriod}>
                      {PERIODS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </SelectShell>
                  )}
                </div>

                {/* quick price chips */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {QUICK_PRICES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPrice(String(p))}
                      className={`rounded-radius-xs px-2.5 py-1 text-footnote font-medium transition ${
                        "bg-fill-tertiary text-label-secondary hover:bg-fill-quaternary"
                      }`}
                    >
                      {symbol}
                      {p}
                    </button>
                  ))}
                </div>

                <div className={`my-4 border-t ${divider}`} />

                {/* Free trial */}
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <FiGift className={`h-4 w-4 ${subtleText}`} />
                    <span className={`text-subheadline-semibold ${baseText}`}>
                      {t("mintSpace.includeFreeTrial") || "Include a free trial"}
                    </span>
                    <span className="rounded-md bg-accent-tinted px-2 py-0.5 text-caption-1-semibold text-accent-text">
                      {t("mintSpace.recommended") || "Recommended"}
                    </span>
                  </span>
                  <Toggle checked={freeTrial} onChange={setFreeTrial} />
                </div>

                {/* Advanced pricing options (collapsible) */}
                <button
                  type="button"
                  onClick={() => setPlanSettingsOpen((o) => !o)}
                  className="mt-3 flex w-full items-center justify-between"
                >
                  <span className="flex items-center gap-2.5">
                    <FiSettings className={`h-4 w-4 ${subtleText}`} />
                    <span className={`text-subheadline-semibold ${baseText}`}>
                      {t("mintSpace.advancedOptions") || "Advanced pricing options"}
                    </span>
                  </span>
                  <FiChevronDown
                    className={`h-4 w-4 transition-transform ${mutedText} ${
                      planSettingsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {planSettingsOpen && (
                  <div className={`mt-3 space-y-4 rounded-radius-sm border p-4 ${cardBase}`}>
                    {/* Free trial length — only when a trial is enabled */}
                    {freeTrial && (
                      <div>
                        <label className={`block text-footnote-semibold text-label-secondary`}>
                          {t("mintSpace.trialLength") || "Free trial length"}
                        </label>
                        <div className="relative mt-1.5 max-w-[180px]">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={trialDays}
                            onChange={(e) => setTrialDays(e.target.value)}
                            placeholder="0"
                            className={`w-full rounded-radius-sm border py-2.5 pl-3 pr-14 text-subheadline outline-none transition ${inputBase}`}
                          />
                          <span
                            className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-footnote ${mutedText}`}
                          >
                            {t("mintSpace.days") || "days"}
                          </span>
                        </div>
                        <p className={`mt-1 text-footnote ${mutedText}`}>
                          {t("mintSpace.trialLengthHint") ||
                            "Number of free days before the first charge."}
                        </p>
                      </div>
                    )}

                    {/* Billing cycles — only for recurring plans */}
                    {billingType === "recurring" && (
                      <div>
                        <label className={`block text-footnote-semibold text-label-secondary`}>
                          {t("mintSpace.billingCycles") || "Billing cycles"}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={billingCycles}
                          onChange={(e) => setBillingCycles(e.target.value)}
                          placeholder={t("mintSpace.unlimited") || "Unlimited"}
                          className={`mt-1.5 w-full max-w-[180px] rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none transition ${inputBase}`}
                        />
                        <p className={`mt-1 text-footnote ${mutedText}`}>
                          {t("mintSpace.billingCyclesHint") ||
                            "Stop billing after this many cycles. Leave empty to bill until cancelled."}
                        </p>
                      </div>
                    )}

                    {isConsultingProduct && (
                      <div className="space-y-3 rounded-radius-sm border border-violet-500/20 bg-violet-500/5 p-3">
                        <p className={`text-subheadline-semibold ${baseText}`}>
                          {t("consulting.productAccessTitle") || "Consulting access"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { id: "time", label: t("consulting.accessTime") || "Time-based" },
                            { id: "quota", label: t("consulting.accessQuota") || "Call quota" },
                            { id: "manual", label: t("consulting.accessManual") || "Until you close" },
                          ].map(({ id, label }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => setConsultingAccessMode(id)}
                              className={`rounded-radius-xs border px-3 py-1.5 text-footnote-semibold ${
                                consultingAccessMode === id
                                  ? "border-violet-400 bg-violet-500/15 text-violet-200"
                                  : "border-separator-default text-label-secondary"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {consultingAccessMode === "time" && (
                          <div>
                            <label className={`text-footnote font-medium ${mutedText}`}>
                              {t("consulting.durationDays") || "Access duration (days)"}
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={consultingDurationDays}
                              onChange={(e) => setConsultingDurationDays(e.target.value)}
                              className={`mt-1 w-full max-w-[140px] rounded-radius-xs border px-2 py-1.5 text-subheadline ${inputBase}`}
                            />
                          </div>
                        )}
                        {consultingAccessMode === "quota" && (
                          <div>
                            <label className={`text-footnote font-medium ${mutedText}`}>
                              {t("consulting.callQuota") || "Included calls"}
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={consultingCallQuota}
                              onChange={(e) => setConsultingCallQuota(e.target.value)}
                              className={`mt-1 w-full max-w-[140px] rounded-radius-xs border px-2 py-1.5 text-subheadline ${inputBase}`}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Limit available spots */}
                    <div>
                      <label className={`block text-footnote-semibold text-label-secondary`}>
                        {t("mintSpace.limitSpots") || "Limit available spots"}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={spotsLimit}
                        onChange={(e) => setSpotsLimit(e.target.value)}
                        placeholder={t("mintSpace.unlimited") || "Unlimited"}
                        className={`mt-1.5 w-full max-w-[180px] rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none transition ${inputBase}`}
                      />
                      <p className={`mt-1 text-footnote ${mutedText}`}>
                        {t("mintSpace.limitSpotsHint") ||
                          "Maximum number of members who can buy. Leave empty for unlimited."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Accept local currency */}
                {/* <div className="mt-3 flex items-center justify-between opacity-60"> */}
                  {/* <span className="flex items-center gap-2.5"> */}
                    {/* <FiGlobe className={`h-4 w-4 ${subtleText}`} /> */}
                    {/* <span className={`text-subheadline-semibold ${baseText}`}>
                      {t("mintSpace.acceptLocalCurrency") || "Accept local currency payments"}
                    </span> */}
                  {/* </span> */}
                  {/* <Toggle checked={acceptLocalCurrency} onChange={setAcceptLocalCurrency} disabled /> */}
                {/* </div> */}

                {/* Customize payment methods */}
                {/* <div className="mt-3 flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <FiCreditCard className={`h-4 w-4 ${subtleText}`} />
                    <span className={`text-subheadline-semibold ${baseText}`}>
                      {t("mintSpace.customizePaymentMethods") || "Customize payment methods"}
                    </span>
                  </span>
                  <Toggle checked={customizePayments} onChange={setCustomizePayments} />
                </div> */}
              </div>
            )}

            {accessType === "free" && (
              <div className={`mt-4 rounded-radius-md border p-5 text-subheadline ${innerCard} ${subtleText}`}>
                {t("mintSpace.freeAccessHint") ||
                  "Anyone can access this product for free once they join your Mint Space."}
              </div>
            )}
          </section>

          {/* FAQ preview (read-only mirror of the editor) */}
          {faq.some((f) => f.question.trim()) && (
            <div className={`border-t pt-4 ${divider}`}>
              <h4 className={`text-subheadline-semibold ${baseText}`}>{t("mintSpace.faq") || "FAQ"}</h4>
              <ul className="mt-2 space-y-2">
                {faq
                  .filter((f) => f.question.trim())
                  .map((f, idx) => (
                    <li key={idx}>
                      <p className={`text-subheadline-semibold ${baseText}`}>{f.question}</p>
                      {f.answer.trim() && (
                        <p className={`text-footnote ${subtleText}`}>{f.answer}</p>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Save / delete */}
          <div className="flex justify-end gap-space-8">
            {selectedProductId && (
              <Button
                variant="gray"
                size="m"
                className="!text-status-danger"
                icon={<Icon name="trash" size={20} />}
                onClick={handleDelete}
                disabled={saving}
              >
                {t("common.delete")}
              </Button>
            )}
            <Button size="m" loading={saving} disabled={bannerUploading || listingBannerUploading} onClick={handleSave}>
              {saving ? t("common.saving") : selectedProductId ? t("common.save") : t("mintSpace.createProduct")}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default MintSpaceAppPricingPage;
